import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const POLL_INTERVAL_MS = 20_000;

const NotificationContext = createContext({
  unreadCount: 0,
  recent: [],
  refreshCount: () => {},
  loadRecent: () => {},
  markRead: async () => {},
  markAllRead: async () => {},
});

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const pollRef = useRef(null);

  const refreshCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread_count");
      setUnreadCount(data.count || 0);
    } catch {
      /* ignore */
    }
  }, [user]);

  const loadRecent = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setRecent(data);
    } catch {
      /* ignore */
    }
  }, [user]);

  const markRead = useCallback(async (id) => {
    setRecent((rs) => rs.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch {
      // best-effort; refresh to reconcile
      refreshCount();
    }
  }, [refreshCount]);

  const markAllRead = useCallback(async () => {
    setRecent((rs) => rs.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await api.post("/notifications/read_all");
    } catch {
      refreshCount();
    }
  }, [refreshCount]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setRecent([]);
      return;
    }
    refreshCount();
    pollRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, refreshCount]);

  return (
    <NotificationContext.Provider
      value={{ unreadCount, recent, refreshCount, loadRecent, markRead, markAllRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
